/**
 * Readiness Definition Resolver
 *
 * Resolves the effective readiness definition from the inheritance chain:
 *   session ?? track ?? cycle ?? workspace
 *
 * Follows the same ?? pattern as effectiveConfigResolver.ts.
 * Each scope can override by setting a readiness_definition_id FK.
 * null means "inherit from parent."
 */

import type {
  ReadinessDefinition,
  ReadinessDefinitionScope,
  EffectiveReadinessDefinition,
} from '../types/prioritization/readinessDefinition';

/**
 * Resolve which definition ID is effective in a given context.
 * Returns the first non-null ID and its source scope, or null if none set.
 */
export function resolveEffectiveReadinessDefinitionId(
  workspaceDefinitionId?: string | null,
  cycleDefinitionId?: string | null,
  trackDefinitionId?: string | null,
  sessionDefinitionId?: string | null
): { id: string; source: ReadinessDefinitionScope } | null {
  if (sessionDefinitionId) return { id: sessionDefinitionId, source: 'session' };
  if (trackDefinitionId) return { id: trackDefinitionId, source: 'track' };
  if (cycleDefinitionId) return { id: cycleDefinitionId, source: 'cycle' };
  if (workspaceDefinitionId) return { id: workspaceDefinitionId, source: 'workspace' };
  return null;
}

/**
 * Given a list of loaded definitions and the FK values from each scope,
 * returns the effective definition and which layer it came from.
 *
 * This is the main entry point used by the store and the readiness page.
 */
export function resolveEffectiveReadinessDefinition(
  definitions: ReadinessDefinition[],
  workspaceDefinitionId?: string | null,
  cycleDefinitionId?: string | null,
  trackDefinitionId?: string | null,
  sessionDefinitionId?: string | null
): EffectiveReadinessDefinition {
  const resolved = resolveEffectiveReadinessDefinitionId(
    workspaceDefinitionId,
    cycleDefinitionId,
    trackDefinitionId,
    sessionDefinitionId
  );

  if (!resolved) return { definition: null, source: 'none' };

  const definition = definitions.find((d) => d.id === resolved.id) ?? null;
  return { definition, source: resolved.source };
}
